import { Button } from '@shared/ui/Button';
import { Card, Field, Input, Muted, Small, Table, TableBox } from '@widgets/ksp-workspace/parts';
import type { KspRdConfirmation, KspRdDocument, KspRdPlan, KspRdRevision } from '@entities/ksp/types';
import { CONDITION_LABEL, revisionName, ru, SOURCE_LABEL } from '../types';

export interface RdDocumentsCardProps {
  canEdit: boolean;
  filter: string;
  setFilter: (f: string) => void;
  documents: KspRdDocument[];
  shown: number;
  setShown: (n: number) => void;
  confirmations: Map<string, KspRdConfirmation[]>;
  planFor: (doc: KspRdDocument) => KspRdPlan | undefined;
  onConfirm: (doc: KspRdDocument, current: KspRdRevision, condition: 'receipt' | 'permit') => void;
  onRevoke: (c: KspRdConfirmation) => void;
  totalDocuments: number;
}

export function RdDocumentsCard({
  canEdit,
  filter,
  setFilter,
  documents,
  shown,
  setShown,
  confirmations,
  planFor,
  onConfirm,
  onRevoke,
  totalDocuments,
}: RdDocumentsCardProps) {
  return (
    <Card>
      <h3>Комплекты реестра РД ПТО и подтверждённые даты</h3>
      <Field>
        Поиск по шифру или наименованию
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} />
      </Field>
      <TableBox>
        <Table>
          <thead>
            <tr>
              <th>Комплект</th>
              <th>Ревизия ПТО</th>
              <th>Кандидаты ПТО</th>
              <th>Подтверждено</th>
              <th>План</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {documents.slice(0, shown).map((doc) => {
              const live = doc.revisions.filter((r) => !r.deleted);
              const current = live.find((r) => r.status === 'active') ?? live[0];
              const plan = planFor(doc);
              return (
                <tr key={doc.id}>
                  <td>
                    {doc.code}
                    <Small>{doc.title}</Small>
                    {doc.deleted && <Small>помечен на удаление в ПТО — даты не участвуют</Small>}
                  </td>
                  <td>
                    {current
                      ? `${revisionName(current)}${current.status === 'active' ? '' : ' (заменена)'} · ${current.ingest_status === 'indexed' ? 'разобрана' : `разбор: ${current.ingest_status}`}`
                      : 'нет действующей ревизии'}
                  </td>
                  <td>
                    {current ? (
                      <>
                        <Small>Загружена в ОСНОВУ {ru(current.candidates.receipt)} — не дата получения</Small>
                        <Small>
                          {current.candidates.permit
                            ? `Штамп «В производство» ${ru(current.candidates.permit)}${current.candidates.permit_signer ? `, ${current.candidates.permit_signer}` : ''}`
                            : current.candidates.permit_raw
                              ? `Штамп: «${current.candidates.permit_raw}» — дата не распознана`
                              : current.ingest_status === 'indexed'
                                ? 'Штампа нет'
                                : 'Штамп появится после разбора'}
                        </Small>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {live.flatMap((rev) =>
                      (confirmations.get(rev.id) ?? []).map((c) => (
                        <Small key={c.id}>
                          {revisionName(rev)}: {CONDITION_LABEL[c.condition].toLowerCase()} {ru(c.confirmed_on)} —{' '}
                          {SOURCE_LABEL[c.source]}
                          {c.confirmed_by_name ? `, ${c.confirmed_by_name}` : ''}{' '}
                          <Button
                            $size="sm"
                            $variant="ghost"
                            disabled={!canEdit}
                            onClick={() => onRevoke(c)}
                          >
                            отменить
                          </Button>
                        </Small>
                      )),
                    )}
                  </td>
                  <td>{plan ? ru(plan.planned_on) : '—'}</td>
                  <td>
                    {current && !doc.deleted && (
                      <>
                        <Button
                          $size="sm"
                          disabled={!canEdit}
                          onClick={() => onConfirm(doc, current, 'receipt')}
                        >
                          Получение
                        </Button>
                        <Button
                          $size="sm"
                          disabled={!canEdit}
                          onClick={() => onConfirm(doc, current, 'permit')}
                        >
                          Разрешение
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableBox>
      {documents.length > shown && (
        <Button $variant="ghost" onClick={() => setShown(shown + 50)}>
          Показать ещё ({documents.length - shown})
        </Button>
      )}
      {totalDocuments === 0 && <Muted>В реестре РД ПТО проекта документов нет.</Muted>}
    </Card>
  );
}
